import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Plus,
  MoreHorizontal,
  Mail,
  Upload,
  Download,
  Filter,
  Trash2,
  Tag,
  CheckSquare,
  Phone,
  XCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ContactImportValidationModal } from './Contacts/ContactImportValidationModal';
import { ColumnMappingDialog } from './Contacts/ColumnMappingDialog';
import { HelpIcon } from './HelpIcon';
import { TooltipProvider } from './ui/tooltip';
import { getHelpContent } from '@/lib/helpContent';
import { useAuth } from '@/contexts/AuthContext';

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  company?: string;
  status: 'active' | 'suppressed' | 'bounced' | 'unsubscribed';
  created_at: string;
  updated_at: string;
  tags: string[];
  custom_fields: Record<string, any>;
}

interface ContactStats {
  total_contacts: number;
  active_contacts: number;
  suppressed_contacts: number;
  bounced_contacts: number;
  unsubscribed_contacts: number;
}

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-800 border-green-200';
    case 'suppressed': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'bounced': return 'bg-red-100 text-red-800 border-red-200';
    case 'unsubscribed': return 'bg-gray-100 text-gray-800 border-gray-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export default function ContactsPage() {
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(25);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [stats, setStats] = useState<ContactStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [saving, setSaving] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [pendingImportFile, setPendingImportFile] = useState<File | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [consentStatus, setConsentStatus] = useState<{
    email?: { consent_status?: string; has_consent: boolean; contact_id?: string; channel?: string };
    sms?: { consent_status?: string; has_consent: boolean; contact_id?: string; channel?: string };
  } | null>(null);
  const [loadingConsent, setLoadingConsent] = useState(false);
  const { tenant } = useAuth();
  const [showImportPresetDialog, setShowImportPresetDialog] = useState(false);
  const [importPresets, setImportPresets] = useState<any[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [packInfo, setPackInfo] = useState<any>(null);
  const [showColumnMappingDialog, setShowColumnMappingDialog] = useState(false);
  const [columnMappingData, setColumnMappingData] = useState<{
    presetMappings: any[];
    csvColumns: string[];
    unmatchedColumns: string[];
    presetName: string;
    file: File;
  } | null>(null);

  // Load pack info and presets when component mounts
  useEffect(() => {
    const loadPackInfo = async () => {
      try {
        const response = await api.verticalPacks.getTenantInfo();
        const info = response.data;
        setPackInfo(info);
        
        // If tenant has a vertical pack (not general), load import mappings
        if (info && !info.is_custom && info.vertical_pack !== 'general') {
          setLoadingPresets(true);
          try {
            const mappingsResponse = await api.verticalPacks.getImportMappings(info.vertical_pack);
            setImportPresets(mappingsResponse.data.presets || []);
          } catch (err) {
            console.error('Failed to load import mappings:', err);
          } finally {
            setLoadingPresets(false);
          }
        }
      } catch (err) {
        console.error('Failed to load pack info:', err);
      }
    };
    loadPackInfo();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [filters.status, filters.search]);

  // Fetch contacts and stats from API
  useEffect(() => {
    fetchContactsAndStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage]);

  const fetchContactsAndStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params for paginated contacts
      const params: any = {
        skip: currentPage * pageSize,
        limit: pageSize + 1, // Fetch one extra to check if there's more
      };

      // Apply filters to API params
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.search) {
        params.search = filters.search;
      }

      // Fetch paginated contacts
      const contactsResponse = await api.contacts.list(params);
      const fetchedContacts = Array.isArray(contactsResponse.data) ? contactsResponse.data : [];
      
      // Check if there's more data
      const hasMoreData = fetchedContacts.length > pageSize;
      const contactsToShow = hasMoreData ? fetchedContacts.slice(0, pageSize) : fetchedContacts;
      
      setContacts(contactsToShow);
      setHasMore(hasMoreData);

      // Fetch stats separately (with a reasonable limit for stats calculation)
      // We'll fetch a larger sample for stats, but not all contacts
      const statsParams: any = {
        limit: 1000, // Sample size for stats
      };
      if (filters.status && filters.status !== 'all') {
        statsParams.status = filters.status;
      }
      if (filters.search) {
        statsParams.search = filters.search;
      }
      
      const statsResponse = await api.contacts.list(statsParams);
      const statsContacts = Array.isArray(statsResponse.data) ? statsResponse.data : [];
      
      // Calculate stats from the sample
      const calculatedStats: ContactStats = {
        total_contacts: statsContacts.length,
        active_contacts: statsContacts.filter((c: Contact) => c.status === 'active').length,
        suppressed_contacts: statsContacts.filter((c: Contact) => c.status === 'suppressed').length,
        bounced_contacts: statsContacts.filter((c: Contact) => c.status === 'bounced').length,
        unsubscribed_contacts: statsContacts.filter((c: Contact) => c.status === 'unsubscribed').length,
      };

      setStats(calculatedStats);
    } catch (err: any) {
      console.error('Error fetching contacts:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load contacts';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to load contacts');
      setContacts([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.email || !newContact.first_name || !newContact.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      await api.contacts.create(newContact);
      toast.success('Contact added successfully');
      setShowAddDialog(false);
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
      });
      fetchContactsAndStats(); // Refresh the list
    } catch (err: any) {
      console.error('Error adding contact:', err);
      
      // Handle error message properly
      let errorMessage = 'Failed to add contact';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors
          errorMessage = errorData.detail.map((e: any) => e.msg).join(', ');
        } else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      toast.info('Preparing export...');
      const response = await api.contacts.exportContacts();
      
      // response.data is already a blob because of responseType: 'blob'
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Contacts exported successfully');
    } catch (err: any) {
      console.error('Error exporting contacts:', err);
      toast.error('Failed to export contacts');
    }
  };

  const handleEditContact = async (contact: Contact) => {
    setSelectedContact(contact);
    setNewContact({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      company: contact.company || '',
    });
    setShowEditDialog(true);
    // Fetch consent status when opening edit dialog
    await fetchConsentStatus(contact.id);
  };

  const fetchConsentStatus = async (contactId: string) => {
    if (!selectedContact) return;
    
    try {
      setLoadingConsent(true);
      const promises: Promise<any>[] = [];
      
      if (selectedContact.email) {
        promises.push(
          api.consent.getConsentStatus(contactId, 'email')
            .then(response => response.data)
            .catch((err) => {
              console.error('Error fetching email consent status:', err);
              return null;
            })
        );
      }
      
      if (selectedContact.phone) {
        promises.push(
          api.consent.getConsentStatus(contactId, 'sms')
            .then(response => response.data)
            .catch((err) => {
              console.error('Error fetching SMS consent status:', err);
              return null;
            })
        );
      }
      
      if (promises.length === 0) {
        setConsentStatus(null);
        return;
      }
      
      const results = await Promise.all(promises);
      
      const status: any = {};
      let resultIndex = 0;
      if (selectedContact.email) {
        status.email = results[resultIndex++] || null;
      }
      if (selectedContact.phone) {
        status.sms = results[resultIndex] || null;
      }
      
      setConsentStatus(status);
    } catch (err) {
      console.error('Error fetching consent status:', err);
      toast.error('Failed to fetch consent status. Please try again.');
      setConsentStatus(null);
    } finally {
      setLoadingConsent(false);
    }
  };

  const handleWithdrawConsent = async (channel: 'email' | 'sms') => {
    if (!selectedContact) {
      toast.error('No contact selected');
      return;
    }
    
    try {
      setLoadingConsent(true);
      console.log(`Withdrawing ${channel} consent for contact ${selectedContact.id}`);
      const response = await api.consent.withdrawConsent(selectedContact.id, channel, 'manual_admin');
      console.log('Withdraw consent response:', response);
      toast.success(`Consent withdrawn for ${channel.toUpperCase()}`, {
        duration: 3000,
      });
      // Refresh consent status after withdrawal
      await fetchConsentStatus(selectedContact.id);
    } catch (err: any) {
      console.error('Error withdrawing consent:', err);
      console.error('Error response:', err.response);
      
      // Extract error message with better handling
      let errorMessage = 'Failed to withdraw consent';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || String(e)).join(', ');
        } else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Show error with better visibility
      toast.error(`Failed to withdraw ${channel.toUpperCase()} consent: ${errorMessage}`, {
        duration: 6000,
        style: {
          background: '#fee2e2',
          color: '#991b1b',
          border: '1px solid #fca5a5',
        },
      });
    } finally {
      setLoadingConsent(false);
    }
  };

  const handleGrantConsent = async (channel: 'email' | 'sms') => {
    if (!selectedContact) {
      toast.error('No contact selected');
      return;
    }
    
    try {
      setLoadingConsent(true);
      console.log(`Granting ${channel} consent for contact ${selectedContact.id}`);
      const response = await api.consent.grantConsent(selectedContact.id, channel, 'manual_admin');
      console.log('Grant consent response:', response);
      toast.success(`Consent granted for ${channel.toUpperCase()}`, {
        duration: 3000,
      });
      // Refresh consent status after granting
      await fetchConsentStatus(selectedContact.id);
    } catch (err: any) {
      console.error('Error granting consent:', err);
      console.error('Error response:', err.response);
      
      // Extract error message with better handling
      let errorMessage = 'Failed to grant consent';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || String(e)).join(', ');
        } else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Show error with better visibility
      toast.error(`Failed to grant ${channel.toUpperCase()} consent: ${errorMessage}`, {
        duration: 6000,
        style: {
          background: '#fee2e2',
          color: '#991b1b',
          border: '1px solid #fca5a5',
        },
      });
    } finally {
      setLoadingConsent(false);
    }
  };

  const handleViewDetails = (contact: Contact) => {
    setSelectedContact(contact);
    setShowDetailsDialog(true);
  };

  const handleChangeStatus = (contact: Contact) => {
    setSelectedContact(contact);
    setShowStatusDialog(true);
  };

  const handleUpdateContact = async () => {
    if (!selectedContact) return;

    try {
      setSaving(true);
      await api.contacts.update(selectedContact.id, newContact);
      toast.success('Contact updated successfully');
      setShowEditDialog(false);
      setSelectedContact(null);
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
      });
      fetchContactsAndStats(); // Refresh the list
    } catch (err: any) {
      console.error('Error updating contact:', err);
      
      let errorMessage = 'Failed to update contact';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg).join(', ');
        }
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!selectedContact) return;

    try {
      setSaving(true);
      await api.contacts.update(selectedContact.id, { status: newStatus });
      toast.success('Contact status updated successfully');
      setShowStatusDialog(false);
      setSelectedContact(null);
      fetchContactsAndStats(); // Refresh the list
    } catch (err: any) {
      console.error('Error updating status:', err);
      toast.error('Failed to update contact status');
    } finally {
      setSaving(false);
    }
  };

  // Bulk operations handlers
  const toggleContactSelection = (contactId: string) => {
    const newSelection = new Set(selectedContacts);
    if (newSelection.has(contactId)) {
      newSelection.delete(contactId);
    } else {
      newSelection.add(contactId);
    }
    setSelectedContacts(newSelection);
    setShowBulkActions(newSelection.size > 0);
  };

  const toggleSelectAll = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedContacts(new Set(contacts.map(c => c.id)));
      setShowBulkActions(true);
    }
  };

  const clearSelection = () => {
    setSelectedContacts(new Set());
    setShowBulkActions(false);
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.size === 0) return;

    if (!confirm(`Are you sure you want to delete ${selectedContacts.size} contact(s)?`)) {
      return;
    }

    try {
      const contactIds = Array.from(selectedContacts);
      const response = await api.contacts.bulkDelete(contactIds);
      const result = response.data;
      
      toast.success(`Successfully deleted ${result.success_count} contact(s)`);
      
      if (result.failed_count > 0) {
        toast.warning(`Failed to delete ${result.failed_count} contact(s)`);
      }
      
      clearSelection();
      fetchContactsAndStats(); // Refresh the list
    } catch (err: any) {
      console.error('Error deleting contacts:', err);
      toast.error('Failed to delete contacts');
    }
  };

  const handleBulkUpdateStatus = async (newStatus: string) => {
    if (selectedContacts.size === 0) return;

    try {
      const contactIds = Array.from(selectedContacts);
      const response = await api.contacts.bulkUpdate(contactIds, { status: newStatus });
      const result = response.data;
      
      toast.success(`Successfully updated ${result.success_count} contact(s)`);
      
      if (result.failed_count > 0) {
        toast.warning(`Failed to update ${result.failed_count} contact(s)`);
      }
      
      clearSelection();
      fetchContactsAndStats(); // Refresh the list
    } catch (err: any) {
      console.error('Error updating contacts:', err);
      toast.error('Failed to update contacts');
    }
  };

  const handleBulkAddTags = async (tags: string[]) => {
    if (selectedContacts.size === 0 || tags.length === 0) return;

    try {
      const contactIds = Array.from(selectedContacts);
      const response = await api.contacts.bulkTag(contactIds, tags, []);
      const result = response.data;
      
      toast.success(`Successfully added tags to ${result.success_count} contact(s)`);
      
      if (result.failed_count > 0) {
        toast.warning(`Failed to tag ${result.failed_count} contact(s)`);
      }
      
      clearSelection();
      fetchContactsAndStats(); // Refresh the list
    } catch (err: any) {
      console.error('Error tagging contacts:', err);
      toast.error('Failed to tag contacts');
    }
  };

  const handleImport = () => {
    // If tenant has a vertical pack with import presets, show preset selector first
    if (packInfo && !packInfo.is_custom && importPresets.length > 0) {
      setShowImportPresetDialog(true);
      return;
    }
    
    // Otherwise, proceed with normal import
    startFileSelection();
  };

  const startFileSelection = (presetName?: string) => {
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }

      // Store the file for later import
      setPendingImportFile(file);

      // If a preset was selected, try using the column mapping endpoints
      if (presetName && packInfo && !packInfo.is_custom) {
        const preset = importPresets.find((p: any) => p.name === presetName);
        if (preset) {
          await performImportWithPreset(file, preset);
          return;
        }
      }

      // Otherwise, use the standard import flow
      // First, validate the import
      const formData = new FormData();
      formData.append('file', file);

      try {
        toast.info('Validating contacts...');
        const response = await api.contacts.validateImport(formData);
        const validation = response.data;
        
        setValidationResult(validation);
        
        // If validation passed, proceed directly with import
        if (validation.validation_passed) {
          toast.success('All contacts validated successfully!');
          await performImport(file);
        } else {
          // Show validation modal for errors
          setShowValidationModal(true);
        }
      } catch (err: any) {
        console.error('Error validating contacts:', err);
        
        // Handle error message properly
        let errorMessage = 'Failed to validate contacts';
        if (err.response?.data) {
          const errorData = err.response.data;
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map((e: any) => e.msg).join(', ');
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        toast.error(errorMessage);
      }
    };

    input.click();
  };

  const performImportWithPreset = async (file: File, preset: any) => {
    try {
      toast.info('Previewing CSV with preset mappings...');
      
      // First preview the CSV with preset mappings to check for matches
      const previewFormData = new FormData();
      previewFormData.append('file', file);
      
      // Convert preset mappings to format expected by API
      const presetMappingsForApi = preset.column_mappings.map((mapping: any) => ({
        column_name: mapping.column_name,
        field_name: mapping.field_name,
        required: mapping.required || false,
        transform: mapping.transform || null
      }));
      
      const previewResponse = await api.imports.previewCsv(previewFormData, presetMappingsForApi);
      const preview = previewResponse.data;
      
      // Check if there are unmatched columns
      if (preview.unmatched_columns && preview.unmatched_columns.length > 0) {
        // Show column mapping dialog
        setColumnMappingData({
          presetMappings: preset.column_mappings.map((m: any) => {
            const matchedMapping = preview.column_mappings.find((cm: any) => 
              cm.field_name === m.field_name
            );
            return {
              ...m,
              csv_column: matchedMapping?.column_name || ''
            };
          }),
          csvColumns: preview.csv_columns || [],
          unmatchedColumns: preview.unmatched_columns || [],
          presetName: preset.name,
          file: file
        });
        setShowColumnMappingDialog(true);
        return;
      }
      
      // All columns matched - proceed with import
      const columnMappings = preview.column_mappings.map((mapping: any) => ({
        column_name: mapping.column_name,
        field_name: mapping.field_name,
        required: mapping.required || false,
        transform: mapping.transform || null
      }));
      
      await processImportWithMappings(file, columnMappings, preset.name);
      
    } catch (err: any) {
      console.error('Error importing with preset:', err);
      // Fall back to standard import
      toast.warning('Preset import failed, falling back to standard import...');
      await performImport(file);
    }
  };

  const processImportWithMappings = async (file: File, columnMappings: any[], presetName?: string) => {
    try {
      toast.info('Importing contacts with preset mappings...');
      const formData = new FormData();
      formData.append('file', file);
      
      const importResponse = await api.imports.processCsv(formData, columnMappings, {
        skip_duplicates: true,
        update_existing: false
      });
      
      const result = importResponse.data;
      toast.success(`Successfully imported contacts${presetName ? ` using ${presetName} preset` : ''}!`);
      fetchContactsAndStats(); // Refresh the list
    } catch (err: any) {
      console.error('Error processing import:', err);
      toast.error('Failed to import contacts. Please try again.');
    }
  };

  const handleColumnMappingConfirm = async (mappings: any[]) => {
    if (!columnMappingData) return;
    
    setShowColumnMappingDialog(false);
    await processImportWithMappings(columnMappingData.file, mappings, columnMappingData.presetName);
    setColumnMappingData(null);
  };

  const performImport = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.info('Importing contacts...');
      const response = await api.contacts.importContacts(formData);
      
      // Handle response
      const result = response.data;
      if (result.imported_count > 0) {
        toast.success(`Successfully imported ${result.imported_count} contacts`);
        fetchContactsAndStats(); // Refresh the list
      } else {
        toast.warning('No contacts were imported');
      }
      
      // Show errors if any
      if (result.errors && result.errors.length > 0) {
        console.warn('Import errors:', result.errors);
        toast.warning(`${result.errors.length} rows had errors. Check console for details.`);
      }
    } catch (err: any) {
      console.error('Error importing contacts:', err);
      
      // Handle error message properly
      let errorMessage = 'Failed to import contacts';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg).join(', ');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleImportValidOnly = async () => {
    if (!pendingImportFile) return;
    
    setShowValidationModal(false);
    await performImport(pendingImportFile);
    setPendingImportFile(null);
    setValidationResult(null);
  };

  const handleFixAndReupload = () => {
    setShowValidationModal(false);
    setPendingImportFile(null);
    setValidationResult(null);
    // User can click import again to select a new file
  };

  const contactsList = contacts;

  const contactImportHelp = getHelpContent('contactImport');
  const zapierHelp = getHelpContent('contactImportZapier');

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Contacts</h1>
            </div>
            <p className="text-muted-foreground">
              Manage your contact database and communication preferences
            </p>
          </div>
          <div className="flex gap-2">
            <div className="flex items-center gap-1">
              <Button variant="outline" onClick={handleImport}>
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              {contactImportHelp && (
                <HelpIcon
                  tooltip="Learn about importing contacts"
                  modalTitle={contactImportHelp.title}
                  modalContent={
                    <div>
                      <p className="mb-4 whitespace-pre-wrap">{contactImportHelp.content}</p>
                      {zapierHelp && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-semibold mb-2">{zapierHelp.title}</h4>
                          <p className="whitespace-pre-wrap">{zapierHelp.content}</p>
                        </div>
                      )}
                    </div>
                  }
                  size="sm"
                  variant="subtle"
                />
              )}
            </div>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {showBulkActions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {selectedContacts.size} contact(s) selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    <Tag className="h-4 w-4 mr-2" />
                    Change Status
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleBulkUpdateStatus('active')}>
                    Active
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkUpdateStatus('suppressed')}>
                    Suppressed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkUpdateStatus('bounced')}>
                    Bounced
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkUpdateStatus('unsubscribed')}>
                    Unsubscribed
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" variant="outline" onClick={handleBulkDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={clearSelection}>
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_contacts}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <div className="h-4 w-4 bg-green-500 rounded-full" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active_contacts}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Suppressed</CardTitle>
              <div className="h-4 w-4 bg-yellow-500 rounded-full" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.suppressed_contacts}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bounced</CardTitle>
              <div className="h-4 w-4 bg-red-500 rounded-full" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.bounced_contacts}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unsubscribed</CardTitle>
              <div className="h-4 w-4 bg-gray-500 rounded-full" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unsubscribed_contacts}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search contacts..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suppressed">Suppressed</SelectItem>
                  <SelectItem value="bounced">Bounced</SelectItem>
                  <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Contacts List</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading contacts...
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              {error}
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedContacts.size === contacts.length && contacts.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contactsList.map((contact: Contact) => (
                    <TableRow key={contact.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedContacts.has(contact.id)}
                          onChange={() => toggleContactSelection(contact.id)}
                          className="rounded"
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {contact.first_name} {contact.last_name}
                      </TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.phone || '-'}</TableCell>
                      <TableCell>{contact.company || '-'}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(contact.status)}>
                          {contact.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditContact(contact)}>
                              Edit Contact
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewDetails(contact)}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeStatus(contact)}>
                              Change Status
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {contactsList.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No contacts found matching your criteria.
                </div>
              )}
              
              {/* Pagination Controls */}
              {contactsList.length > 0 && (
                <div className="flex items-center justify-between px-2 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, (currentPage * pageSize) + contactsList.length)} of {hasMore ? 'many' : (currentPage * pageSize) + contactsList.length} contacts
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0 || loading}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-muted-foreground px-2">
                        Page {currentPage + 1}
                        {hasMore && ' +'}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!hasMore || loading}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Contact Dialog */}
      {showAddDialog && (
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-[500px] bg-white">
            <DialogHeader>
              <DialogTitle>Add New Contact</DialogTitle>
              <DialogDescription>
                Create a new contact in your database
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={newContact.first_name}
                  onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                  placeholder="John"
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={newContact.last_name}
                  onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                  placeholder="Doe"
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="john.doe@example.com"
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  placeholder="+1-555-0123"
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={newContact.company}
                  onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                  placeholder="Acme Corp"
                  className="border-gray-300"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddContact} disabled={saving}>
                {saving ? 'Adding...' : 'Add Contact'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Contact Dialog */}
      {showEditDialog && selectedContact && (
        <Dialog open={showEditDialog} onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) {
            setConsentStatus(null);
            setSelectedContact(null);
          }
        }}>
          <DialogContent className="sm:max-w-[500px] bg-white">
            <DialogHeader>
              <DialogTitle>Edit Contact</DialogTitle>
              <DialogDescription>
                Update contact information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit_first_name">First Name</Label>
                <Input
                  id="edit_first_name"
                  value={newContact.first_name}
                  onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_last_name">Last Name</Label>
                <Input
                  id="edit_last_name"
                  value={newContact.last_name}
                  onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_phone">Phone</Label>
                <Input
                  id="edit_phone"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_company">Company</Label>
                <Input
                  id="edit_company"
                  value={newContact.company}
                  onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                  className="border-gray-300"
                />
              </div>

              {/* Consent Management Section */}
              <div className="pt-4 border-t border-gray-200">
                <Label className="text-base font-semibold mb-3 block">Consent Management</Label>
                <div className="space-y-3">
                  {/* Email Consent */}
                  {selectedContact?.email && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-600" />
                        <div>
                          <div className="font-medium text-sm">Email Consent</div>
                          <div className="text-xs text-gray-500">
                            {loadingConsent ? 'Loading...' : 
                             consentStatus?.email?.has_consent 
                               ? `Status: ${consentStatus.email.consent_status || 'granted'}` 
                               : consentStatus?.email 
                               ? `Status: ${consentStatus.email.consent_status || 'withdrawn'}`
                               : 'Status: unknown'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {loadingConsent ? (
                          <div className="text-xs text-gray-400">Loading...</div>
                        ) : consentStatus?.email?.has_consent === true ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleWithdrawConsent('email')}
                              disabled={loadingConsent}
                              className="text-xs"
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              Withdraw
                            </Button>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-red-600" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGrantConsent('email')}
                              disabled={loadingConsent}
                              className="text-xs"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Grant
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* SMS Consent */}
                  {selectedContact?.phone && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-600" />
                        <div>
                          <div className="font-medium text-sm">SMS Consent</div>
                          <div className="text-xs text-gray-500">
                            {loadingConsent ? 'Loading...' : 
                             consentStatus?.sms?.has_consent 
                               ? `Status: ${consentStatus.sms.consent_status || 'granted'}` 
                               : consentStatus?.sms 
                               ? `Status: ${consentStatus.sms.consent_status || 'withdrawn'}`
                               : 'Status: unknown'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {loadingConsent ? (
                          <div className="text-xs text-gray-400">Loading...</div>
                        ) : consentStatus?.sms?.has_consent === true ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleWithdrawConsent('sms')}
                              disabled={loadingConsent}
                              className="text-xs"
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              Withdraw
                            </Button>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-red-600" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGrantConsent('sms')}
                              disabled={loadingConsent}
                              className="text-xs"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Grant
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {!selectedContact?.email && !selectedContact?.phone && (
                    <div className="text-sm text-gray-500 italic p-2">
                      No email or phone number available for consent management
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateContact} disabled={saving}>
                {saving ? 'Updating...' : 'Update Contact'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* View Details Dialog */}
      {showDetailsDialog && selectedContact && (
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="sm:max-w-[500px] bg-white">
            <DialogHeader>
              <DialogTitle>Contact Details</DialogTitle>
              <DialogDescription>
                View complete contact information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="font-medium text-gray-700">Name:</div>
                <div className="col-span-2">{selectedContact.first_name} {selectedContact.last_name}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="font-medium text-gray-700">Email:</div>
                <div className="col-span-2">{selectedContact.email}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="font-medium text-gray-700">Phone:</div>
                <div className="col-span-2">{selectedContact.phone || '-'}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="font-medium text-gray-700">Company:</div>
                <div className="col-span-2">{selectedContact.company || '-'}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="font-medium text-gray-700">Status:</div>
                <div className="col-span-2">
                  <Badge className={getStatusColor(selectedContact.status)}>
                    {selectedContact.status}
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="font-medium text-gray-700">Created:</div>
                <div className="col-span-2">
                  {new Date(selectedContact.created_at).toLocaleString()}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="font-medium text-gray-700">Updated:</div>
                <div className="col-span-2">
                  {new Date(selectedContact.updated_at).toLocaleString()}
                </div>
              </div>
              {selectedContact.tags && selectedContact.tags.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  <div className="font-medium text-gray-700">Tags:</div>
                  <div className="col-span-2 flex gap-1 flex-wrap">
                    {selectedContact.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setShowDetailsDialog(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Change Status Dialog */}
      {showStatusDialog && selectedContact && (
        <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <DialogContent className="sm:max-w-[400px] bg-white">
            <DialogHeader>
              <DialogTitle>Change Contact Status</DialogTitle>
              <DialogDescription>
                Update the status for {selectedContact.first_name} {selectedContact.last_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Current Status</Label>
                <div>
                  <Badge className={getStatusColor(selectedContact.status)}>
                    {selectedContact.status}
                  </Badge>
                </div>
              </div>
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select onValueChange={handleUpdateStatus}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suppressed">Suppressed</SelectItem>
                    <SelectItem value="bounced">Bounced</SelectItem>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Import Preset Selection Dialog */}
      <Dialog open={showImportPresetDialog} onOpenChange={setShowImportPresetDialog}>
        <DialogContent className="sm:max-w-[600px] bg-white">
          <DialogHeader>
            <DialogTitle>Select PMS/Booking System Preset</DialogTitle>
            <DialogDescription>
              Choose a preset to auto-map your CSV columns. This will help ensure your data is imported correctly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {loadingPresets ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="preset-select">Preset</Label>
                  <Select value={selectedPreset} onValueChange={setSelectedPreset}>
                    <SelectTrigger id="preset-select">
                      <SelectValue placeholder="Select a preset (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None - Use standard import</SelectItem>
                      {importPresets.map((preset: any) => (
                        <SelectItem key={preset.name} value={preset.name}>
                          {preset.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedPreset && (() => {
                  const preset = importPresets.find((p: any) => p.name === selectedPreset);
                  return preset ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      {preset.description && (
                        <p className="text-sm text-blue-800 mb-3">{preset.description}</p>
                      )}
                      <p className="text-sm font-medium text-blue-900 mb-2">Expected Columns:</p>
                      <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                        {preset.column_mappings?.map((mapping: any, idx: number) => (
                          <li key={idx}>
                            <strong>{mapping.column_name}</strong> → {mapping.field_name}
                            {mapping.required && <span className="text-red-600 ml-1">*</span>}
                          </li>
                        ))}
                      </ul>
                      {preset.notes && (
                        <p className="text-xs text-blue-700 mt-3 italic border-t border-blue-200 pt-3">
                          {preset.notes}
                        </p>
                      )}
                    </div>
                  ) : null;
                })()}
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => {
                    setShowImportPresetDialog(false);
                    setSelectedPreset('');
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={() => {
                    const presetToUse = selectedPreset || undefined;
                    setShowImportPresetDialog(false);
                    setSelectedPreset('');
                    startFileSelection(presetToUse);
                  }}>
                    Continue
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Import Validation Modal */}
      <ContactImportValidationModal
        open={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        validationResult={validationResult}
        onImportValidOnly={handleImportValidOnly}
        onFixAndReupload={handleFixAndReupload}
      />

      {/* Column Mapping Dialog */}
      {columnMappingData && (
        <ColumnMappingDialog
          open={showColumnMappingDialog}
          onClose={() => {
            setShowColumnMappingDialog(false);
            setColumnMappingData(null);
          }}
          onConfirm={handleColumnMappingConfirm}
          presetMappings={columnMappingData.presetMappings}
          csvColumns={columnMappingData.csvColumns}
          unmatchedColumns={columnMappingData.unmatchedColumns}
          presetName={columnMappingData.presetName}
        />
      )}
      </div>
    </TooltipProvider>
  );
}
